import React from 'react';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import { Button, Stack, Typography, createTheme, useMediaQuery } from '@mui/material';

import { EditSiteDetails } from '../EditSiteDetails';
import { ReadOnlySiteDetails } from '../ReadOnlySiteDetails';
import { EditAdditionalServiceRequired } from '../EditAdditionalServiceRequired';
import { ReadOnlyAdditionalServiceRequired } from '../ReadOnlyAdditionalServiceRequired';
import { calculateCompletionPercentage } from '../../../utils/helper';
import BorderLinearProgress from '../../common/BorderLinearProgress';
import { EditBuildingDesignDetails } from '../EditBuildingDesignDetails';
import { ReadOnlyBuildingDesignDetails } from '../ReadOnlyBuildingDesignDetails';
import { EditResidentialBuildingDetails } from '../EditResidentialBuildingDetails';
import { ReadOnlyResidentialBuildingDetails } from '../ReadOnlyResidentialBuildingDetails';
import { EditConstructionSpecification } from '../EditConstructionSpecification';
import { ReadOnlyConstructionSpecification } from '../ReadOnlyConstructionSpecification';

const SiteDetails = ({
  handleEditBuildingDesignDetails,
  leadDetails,
  handleEditConstructionSpecification,
  editConstructionSpecification,
  editResidentialBuildingDetails,
  handleEditResidentialBuildingDetails,
  editSiteDetails,
  editAdditionalServiceRequired,
  handleEditSiteDetails,
  handleEditAdditionalServiceRequired,
  editBuildingDesignDetails,
}) => {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const CustomButton = styled(Button)({
    backgroundColor: '#F07B0E',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#f1785a',
    },
    width: 'fit-content',
    alignSelf: isSmallScreen ? 'center' : 'end',
  });
  return (
    <>
      {/* Site Details: Site Details & Documents Section */}
      <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Typography sx={{ fontWeight: '600', alignSelf: 'start' }} component="p">
              Site Details & Documents
            </Typography>
            {editSiteDetails ? (
              ''
            ) : (
              <CustomButton sx={{ ml: 'auto' }} variant="contained" size="small" onClick={handleEditSiteDetails}>
                Edit
              </CustomButton>
            )}
          </Stack>
          <BorderLinearProgress
            color="primary"
            variant="determinate"
            value={calculateCompletionPercentage(leadDetails, [
              'siteAddress',
              'siteNumber',
              'siteOwnerNamePrimary',
              'siteOwnerNameSecondary',
              'siteCondition',
              'constructionSiteGpsLocation',
              'documentsReceived',
              'clientPersonalDocuments',
            ])}
          />
          {editSiteDetails ? (
            <EditSiteDetails
              details={leadDetails}
              leadId={leadDetails.id}
              handleEditSiteDetails={handleEditSiteDetails}
            ></EditSiteDetails>
          ) : (
            <ReadOnlySiteDetails details={leadDetails}></ReadOnlySiteDetails>
          )}
        </Paper>
      </Stack>
      {/* Site Details: Site Documents Additional service required Section */}
      <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Typography sx={{ fontWeight: '600', alignSelf: 'start' }} component="p">
              Additional Service Required
            </Typography>
            {editAdditionalServiceRequired ? (
              ''
            ) : (
              <CustomButton
                sx={{ ml: 'auto' }}
                variant="contained"
                size="small"
                onClick={handleEditAdditionalServiceRequired}
              >
                Edit
              </CustomButton>
            )}
          </Stack>
          <BorderLinearProgress
            color="primary"
            variant="determinate"
            value={calculateCompletionPercentage(leadDetails, ['documentsToBeApplied', 'constructionApproval'])}
          />
          {editAdditionalServiceRequired ? (
            <EditAdditionalServiceRequired
              details={leadDetails}
              leadId={leadDetails.id}
              handleEditAdditionalServiceRequired={handleEditAdditionalServiceRequired}
            ></EditAdditionalServiceRequired>
          ) : (
            <ReadOnlyAdditionalServiceRequired details={leadDetails}></ReadOnlyAdditionalServiceRequired>
          )}
        </Paper>
      </Stack>
      {/* Site Details: Building Design Details Section */}
      <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Typography sx={{ fontWeight: '600', alignSelf: 'start' }} component="p">
              Building Design Details
            </Typography>
            {editBuildingDesignDetails ? (
              ''
            ) : (
              <CustomButton
                sx={{ ml: 'auto' }}
                variant="contained"
                size="small"
                onClick={handleEditBuildingDesignDetails}
              >
                Edit
              </CustomButton>
            )}
          </Stack>
          <BorderLinearProgress
            color="primary"
            variant="determinate"
            value={calculateCompletionPercentage(leadDetails, [
              'orientation',
              'buildingTypology',
              'slabAreaLimitations',
              'occupancyType',
              'vastuCompliance',
            ])}
          />
          {editBuildingDesignDetails ? (
            <EditBuildingDesignDetails
              details={leadDetails}
              leadId={leadDetails.id}
              handleEditDetails={handleEditBuildingDesignDetails}
            ></EditBuildingDesignDetails>
          ) : (
            <ReadOnlyBuildingDesignDetails details={leadDetails}></ReadOnlyBuildingDesignDetails>
          )}
        </Paper>
      </Stack>
      {/* Site Details: Residential Building Details Section */}
      <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Typography sx={{ fontWeight: '600', alignSelf: 'start' }} component="p">
              Residential Building Details
            </Typography>
            {editResidentialBuildingDetails ? (
              ''
            ) : (
              <CustomButton
                sx={{ ml: 'auto' }}
                variant="contained"
                size="small"
                onClick={handleEditResidentialBuildingDetails}
              >
                Edit
              </CustomButton>
            )}
          </Stack>
          <BorderLinearProgress
            color="primary"
            variant="determinate"
            value={calculateCompletionPercentage(leadDetails, [
              'groundFloor',
              'livingRoom',
              'commonToilet',
              'kitchen',
              'prayerRoom',
              'staircase',
              'lift',
              'bedRoom',
              'bedRoomAdditionalDetails',
              'utility',
              'laundry',
              'gym',
              'homeTheater',
              'additionalDetails',
            ])}
          />
          {editResidentialBuildingDetails ? (
            <EditResidentialBuildingDetails
              details={leadDetails}
              leadId={leadDetails.id}
              handleEditDetails={handleEditResidentialBuildingDetails}
            ></EditResidentialBuildingDetails>
          ) : (
            <ReadOnlyResidentialBuildingDetails details={leadDetails}></ReadOnlyResidentialBuildingDetails>
          )}
        </Paper>
      </Stack>
      {/* Site Details: Construction specification Section */}
      <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Typography sx={{ fontWeight: '600', alignSelf: 'start' }} component="p">
              Construction Specification
            </Typography>
            {editConstructionSpecification ? (
              ''
            ) : (
              <CustomButton
                sx={{ ml: 'auto' }}
                variant="contained"
                size="small"
                onClick={handleEditConstructionSpecification}
              >
                Edit
              </CustomButton>
            )}
          </Stack>
          <BorderLinearProgress
            color="primary"
            variant="determinate"
            value={calculateCompletionPercentage(leadDetails, ['masonry', 'rainWaterHarvesting', 'windows'])}
          />
          {editConstructionSpecification ? (
            <EditConstructionSpecification
              details={leadDetails}
              leadId={leadDetails.id}
              handleEditDetails={handleEditConstructionSpecification}
            ></EditConstructionSpecification>
          ) : (
            <ReadOnlyConstructionSpecification details={leadDetails}></ReadOnlyConstructionSpecification>
          )}
        </Paper>
      </Stack>
    </>
  );
};

export default SiteDetails;
